import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  StopCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { Button } from "../../../components/forms/Buttons/Button";
import { isLiveJob } from "../../../services/bookletPrintService";
import { formatDateTime } from "../../../utils/formatDate";
import { focusRing, linkButtonClass, Spinner } from "../../Exams/TestBuilder/ui";
import { linkTapClass, variantHref } from "../bts";
import { groupByVariant, jobErrorLabel, personName, placeLabel, problemLabel, stageLabel, warningLabel } from "./printText";
import { BusyButton, Notice, phoneButtonClass } from "./ui";

// Shown at first in a long list of problems or warnings; the rest behind "show all".
const SHOWN = 6;

// What the renderer finds in each variant's pages, so its variant codes mean something. The content's own problems
// (pictures, glyphs) are reported once for all variants, with the first variant's code only.
const PER_VARIANT = ["image_failed", "katex_error", "page_error", "blocked_request", "content_scaled", "question_split"];

// Problems or warnings, each with its place ("МАТЕМАТИКА, 14-тапсырма: ...") and a link to its subject's variant in
// the bank (in a new tab: the job stays shown here), the same one in several variants once (with their codes when it
// is found per variant and not in all of them). Secondary text is slate-600: slate-500 is under 4.5:1 on red-50.
function PlaceList({ booklet, items, labelOf, totalVariants, tone }) {
  const [t] = useTranslation();
  const [all, setAll] = useState(false);
  const groups = groupByVariant(items);
  const shown = all ? groups : groups.slice(0, SHOWN);
  return (
    <>
      <ul className="mt-2 space-y-1.5">
        {shown.map(({ item, variants }, index) => {
          const extra = item.src || item.title || item.detail;
          const hasSection = item.section !== null && item.section !== undefined;
          const variant = hasSection ? booklet?.check?.sections?.[item.section]?.variant : null;
          return (
            <li key={index} className="flex min-w-0 gap-2">
              <span className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${tone === "error" ? "bg-red-400" : "bg-amber-400"}`} aria-hidden="true" />
              <span className="min-w-0">
                <span className="font-medium text-slate-900">{placeLabel(t, booklet, item)}</span>
                {": "}
                {labelOf(item)}
                {PER_VARIANT.includes(item.code) && hasSection && variants.length > 0 && variants.length < totalVariants && (
                  <span className="text-slate-600"> ({t("bts.print.where.in_variants", { codes: variants.join(", "), count: variants.length })})</span>
                )}
                {variant?.uuid && variant.folder?.uuid && (
                  <>
                    {" "}
                    <Link
                      to={variantHref(variant.folder.uuid, variant.uuid)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${linkButtonClass} ${linkTapClass} -my-1 whitespace-nowrap`}
                    >
                      {t("bts.check.open_variant")}
                      {(variant.name || variant.heading) && <span className="sr-only">: {variant.name || variant.heading}</span>}
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </Link>
                  </>
                )}
                {extra && (
                  <span className="block truncate font-mono text-xs text-slate-600" title={extra}>
                    {extra}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {groups.length > SHOWN && (
        <button
          type="button"
          aria-expanded={all}
          onClick={() => setAll((value) => !value)}
          className={`mt-2 inline-flex items-center rounded-lg px-1.5 py-1 text-sm font-medium text-blue-700 hover:bg-white/70 hover:text-blue-800 max-sm:min-h-[2.75rem] ${focusRing}`}
        >
          {all ? t("bts.print.show_less") : t("bts.print.show_all", { n: groups.length })}
        </button>
      )}
    </>
  );
}

// "Stopping…" stays as it is, "Queued" becomes "Queued.".
export const sentence = (text) => (/[.…!?]$/.test(text) ? text : `${text}.`);

// What a screen reader is told about the job (a polite live region): its stage while under way, and how it
// ended when the page watched it end ("done" only while its documents are what the booklet prints).
export const jobAnnouncement = (t, job, { stalled, pollError, ended, fresh }) => {
  if (!job) return "";
  if (isLiveJob(job)) {
    if (stalled) return t("bts.print.stalled_title");
    if (pollError) return t("bts.print.poll_error");
    const title = job.preview ? t("bts.print.job_title_preview") : t("bts.print.job_title_final");
    const stage = job.cancel_requested ? t("bts.print.cancelling") : stageLabel(t, job.stage);
    const total = job.total || 0;
    return [title, stage, t("bts.print.progress", { done: Math.min(job.done || 0, total), total })].map(sentence).join(" ");
  }
  if (!ended) return "";
  if (job.status === "failed") return `${t("bts.print.failed_title")}. ${jobErrorLabel(t, job.error?.code)}`;
  if (job.status === "cancelled") return t("bts.print.cancelled");
  if (!fresh) return "";
  const warnings = groupByVariant(job.warnings).length;
  if (warnings) return t("bts.print.warnings_title", { n: warnings });
  return job.preview ? t("bts.print.done_preview") : t("bts.print.done_final");
};

// Who asked for the job and when.
function Requested({ job }) {
  const [t, i18n] = useTranslation();
  if (!job?.created) return null;
  const name = personName(job.requested_by);
  return (
    <p className="mt-1 text-xs text-slate-600">
      {name
        ? t("bts.print.job_requested_by", { date: formatDateTime(job.created, i18n.language), name })
        : t("bts.print.job_requested", { date: formatDateTime(job.created, i18n.language) })}
    </p>
  );
}

/**
 * The booklet's latest print job: under way (stage, progress, "Stop"), taking too long or lost track of
 * ("Try again" polls it again), failed (why and where; the section's own button makes a new one, and the notice
 * names it: `retryLabel`, its label now, "" while it cannot be pressed), or how it ended when the page watched it
 * end. A cancelled job (status "cancelled", error null) is no failure: "stopped" is said to whoever watched it stop.
 * `ended`: it ended while the page watched it (then "cancelled" and "done" are said);
 * `current`: its documents are still the booklet's (else its warnings are about replaced files and are not shown);
 * `fresh`: they are also still what the booklet prints (else "done" is not said: the booklet was activated, returned
 * to draft or changed since). The buttons stay focusable while their request runs, and carry `data-print-focus`
 * ("cancel", "retry"): where the page puts the focus when the control that had it goes.
 */
export default function JobPanel({ booklet, job, stalled, pollError, ended, current, fresh, cancelBusy, onCancel, onResume, retryLabel = "" }) {
  const [t] = useTranslation();
  if (!job) return null;
  const total = job.total || 0;
  const done = Math.min(job.done || 0, total);
  const title = job.preview ? t("bts.print.job_title_preview") : t("bts.print.job_title_final");
  const cancelButton = (
    <BusyButton
      type="button"
      color="secondary-outline"
      Icon={StopCircleIcon}
      text={job.cancel_requested ? t("bts.print.cancelling") : t("bts.print.cancel")}
      onClick={onCancel}
      busy={cancelBusy || !!job.cancel_requested}
      data-print-focus="cancel"
      className={phoneButtonClass}
    />
  );

  if (isLiveJob(job) && (stalled || pollError)) {
    return (
      <Notice
        tone="warning"
        Icon={ExclamationTriangleIcon}
        title={stalled ? t("bts.print.stalled_title") : t("bts.print.poll_error")}
        actions={
          <>
            <Button
              type="button"
              color="secondary-outline"
              Icon={ArrowPathIcon}
              text={t("bts.print.retry")}
              onClick={onResume}
              data-print-focus="retry"
              className={phoneButtonClass}
            />
            {cancelButton}
          </>
        }
      >
        <p className="mt-1">{stalled ? t("bts.print.stalled_text") : t("bts.print.poll_error_text")}</p>
        <p className="mt-1 text-slate-600">
          {t("bts.print.last_known_stage", { stage: stageLabel(t, job.stage), done, total })}
        </p>
      </Notice>
    );
  }

  if (isLiveJob(job)) {
    const percent = total ? Math.round((done / total) * 100) : 0;
    return (
      <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <Spinner className="mt-0.5 h-5 w-5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">{title}</p>
              <p className="mt-0.5 text-sm text-slate-700">
                {job.cancel_requested ? t("bts.print.cancelling") : stageLabel(t, job.stage)}
              </p>
              <Requested job={job} />
            </div>
          </div>
          <div className="flex shrink-0">{cancelButton}</div>
        </div>
        <div className="mt-3">
          <div
            role="progressbar"
            aria-label={t("bts.print.progress_label")}
            aria-valuemin={0}
            aria-valuemax={total || 1}
            aria-valuenow={done}
            aria-valuetext={t("bts.print.progress", { done, total })}
            className="h-2 overflow-hidden rounded-full bg-blue-100"
          >
            <div
              className="h-full rounded-full bg-blue-600 transition-[width] duration-500 ease-out"
              style={{ width: `${Math.max(percent, 3)}%` }}
            />
          </div>
          <p className="mt-1.5 text-xs tabular-nums text-slate-600">{t("bts.print.progress", { done, total })}</p>
        </div>
      </div>
    );
  }

  // Made again with the section's own button above (the same action): nothing to press here, but the notice, whose
  // texts say "try again", names that button.
  if (job.status === "failed") {
    const problems = job.problems || [];
    return (
      <Notice tone="error" Icon={XCircleIcon} title={t("bts.print.failed_title")}>
        <p className="mt-1">{jobErrorLabel(t, job.error?.code)}</p>
        {problems.length > 0 && (
          <PlaceList booklet={booklet} items={problems} labelOf={(item) => problemLabel(t, item.code)} totalVariants={total} tone="error" />
        )}
        {retryLabel && <p className="mt-2">{t("bts.print.failed_retry_hint", { button: retryLabel })}</p>}
        {job.error?.detail && job.error.code !== "booklet_content" && (
          <details className="mt-2 text-xs text-slate-600">
            <summary className={`cursor-pointer rounded max-sm:py-3.5 ${focusRing}`}>{t("bts.print.technical_details")}</summary>
            <p className="mt-1 break-words font-mono">{job.error.detail}</p>
          </details>
        )}
        <Requested job={job} />
      </Notice>
    );
  }

  // Said to whoever watched it stop, not on every later visit.
  if (job.status === "cancelled") {
    return ended ? (
      <Notice tone="muted" Icon={NoSymbolIcon} title={t("bts.print.cancelled")}>
        <Requested job={job} />
      </Notice>
    ) : null;
  }

  const warnings = job.warnings || [];
  if (warnings.length > 0 && current) {
    return (
      <Notice tone="warning" Icon={ExclamationTriangleIcon} title={t("bts.print.warnings_title", { n: groupByVariant(warnings).length })}>
        <p className="mt-1">{ended && fresh ? t("bts.print.warnings_text_done") : t("bts.print.warnings_text")}</p>
        <PlaceList booklet={booklet} items={warnings} labelOf={(item) => warningLabel(t, item)} totalVariants={total} tone="warning" />
        <Requested job={job} />
      </Notice>
    );
  }
  if (ended && fresh) {
    return <Notice tone="success" Icon={CheckCircleIcon} title={job.preview ? t("bts.print.done_preview") : t("bts.print.done_final")} />;
  }
  return null;
}
